import { GatedQueryError } from "../components/GatedQueryError";
import {
  Badge,
  BlockStack,
  Banner,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  InlineStack,
  Layout,
  Modal,
  Page,
  Pagination,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";
import {
  PURCHASE_ORDER_PAGE_LIMIT,
  PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS,
  PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES,
  assignVariantSupplier,
  emailPurchaseOrderDraft,
  getSupplierStockyNotes,
  getPurchaseOrderEmailPreview,
  listPurchaseOrderDrafts,
  listOrderableVariants,
  listSuppliers,
  setProductTaxCategory,
  setSupplierInvoiceNo,
  setSupplierContactEmail,
  setVariantCost,
  setVariantCosts,
  type PurchaseOrderDraft,
  type PurchaseOrderEmailPreview,
  type PurchaseOrderEmailAttachment,
  type PurchaseOrderEmailSendResult,
  type PurchaseOrderLine,
  type SeasonPreview,
} from "../api/purchase_orders";
import {
  getPurchaseOrderEmailDelivery,
  purchaseOrderEmailReconciliationKeys,
} from "../api/purchase_order_email_reconciliation";
import { listLocations } from "../api/locations";
import { toLocationSelectOptions } from "../utils/locationOptions";
import { getMe } from "../api/me";
import {
  buildPurchaseOrderCsv,
  buildShopifyPoImportCsv,
  countShopifyPoImportSkippedLines,
  purchaseOrderCsvFilename,
  shopifyPoImportCsvFilename,
} from "../utils/purchaseOrderCsv";
import { downloadTextFile, todayIso } from "../utils/downloadFile";
import { buildPurchaseOrderHtml } from "../utils/purchaseOrderPdf";
import { printBarcodeLabels } from "../utils/printBarcodeLabels";
import { LineSortSelect } from "../components/LineSortSelect";
import { sortLines, type LineSortKey } from "../utils/lineSort";
import {
  BarcodeLabelResultBanner,
  type BarcodeLabelReport,
} from "../components/BarcodeLabelResultBanner";
import { printHtmlDocument } from "../utils/printHtml";
import {
  applyDraftEdits,
  collectBulkCostEdits,
  emptyHeader,
  type BulkCostEdits,
  type DraftEdits,
  type EffectivePurchaseOrderDraft,
  type LineEdit,
  type PurchaseOrderHeader,
} from "../utils/purchaseOrderEdits";
import { placePurchaseOrder } from "../api/purchase_order_lifecycle";
import { ApiError } from "../api/client";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";
import {
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";
import {
  addPurchaseOrderEmailDeliveryOverride,
  clearPurchaseOrderEmailAttempt,
  findPurchaseOrderEmailAttemptForSupplier,
  persistPurchaseOrderEmailAttempt,
  PurchaseOrderEmailAttemptConflictError,
  PurchaseOrderEmailAttemptStorageError,
  requirePurchaseOrderEmailReconciliation,
  type PendingPurchaseOrderEmailAttempt,
} from "../utils/purchaseOrderEmailAttempt";
import { formatDateTime } from "../utils/date";
import {
  purchaseOrderReason,
  replenishmentTargetBreakdown,
} from "../utils/purchaseOrderReason";
import {
  DEFAULT_CURRENCY,
  currencySymbol,
  money,
  moneyInputValue,
} from "../utils/money";
import { formatJpDate, transitionalSummary } from "../utils/invoiceTransition";
import {
  SupplierDestinationHeader,
  type DestinationProp,
} from "../components/po/SupplierDestinationHeader";
import { PasteAddProducts } from "../components/po/PasteAddProducts";
import type { PastedProductAdd } from "../utils/pasteAddPreview";
import { CostSummaryCard } from "../components/po/CostSummaryCard";
import { PoDetailsCard } from "../components/po/PoDetailsCard";
import { PurchaseOrderModeSwitch } from "../components/po/PurchaseOrderModeSwitch";
import { SavedPurchaseOrderDrafts } from "../components/po/SavedPurchaseOrderDrafts";
import { SupplierSetupCard } from "../components/po/SupplierSetupCard";
import { NEW_SUPPLIER_OPTION_VALUE } from "../components/po/AddSupplierModal";
import { UpgradeNote } from "../components/PlanGate";
import {
  genericRegistrationNoError,
  invoiceNoError,
  isValidGenericRegistrationNo,
  isValidInvoiceNo,
} from "../utils/invoiceNo";
import { registrationConcept } from "../utils/registrationLabel";

import { currentLocale, t } from "../lib/i18n";
import { useLocationLimit } from "../lib/useLocationLimit";

// 商品検索の打鍵をまとめてから問い合わせる（SupplierSetupCard と同値）。
const PRODUCT_SEARCH_DEBOUNCE_MS = 300;
const PLACE_ORDER_ATTEMPT_SCOPE = "purchase-order:place";

function attachmentTypeForFile(file: File): string | null {
  const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
  const expected = PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES[extension];
  return expected !== undefined && file.type === expected ? expected : null;
}

async function readPurchaseOrderEmailAttachment(
  file: File,
): Promise<PurchaseOrderEmailAttachment> {
  const contentType = attachmentTypeForFile(file);
  if (contentType === null) {
    throw new Error(
      t("添付できるのは PDF、CSV、Excel、Word、PNG、JPEG のみです。"),
    );
  }
  if (!file.size) throw new Error(t("空の添付ファイルは送信できません。"));
  if (file.size > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxBytesPerFile) {
    throw new Error(t("添付ファイル1件は 1 MiB 以下にしてください。"));
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(t("添付ファイルを読み取れませんでした。")));
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error(t("添付ファイルを読み取れませんでした。")));
    reader.readAsDataURL(file);
  });
  const separator = dataUrl.indexOf(",");
  if (separator < 0) throw new Error(t("添付ファイルを読み取れませんでした。"));
  return { filename: file.name, content_type: contentType, content: dataUrl.slice(separator + 1) };
}

function isDefinitiveEmailFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (
    [
      "delivery_rejected",
      "intent_expired",
      "draft_stale",
      "draft_not_found",
      "draft_invalid",
      "invalid_request",
      "invalid_delivery_override",
    ].includes(error.code ?? "")
  ) {
    return true;
  }
  if (
    [
      "delivery_in_progress",
      "delivery_unknown",
      "delivery_ambiguous",
      "delivery_unresolved",
      "idempotency_key_reused",
    ].includes(error.code ?? "")
  ) {
    return false;
  }
  return (
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}

function isDefinitivePlaceFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return (
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}
// 検索結果として一度に並べる候補ボタンの上限。超えた分は「ほか N 件」で示す。
const PRODUCT_SEARCH_RESULT_LIMIT = 8;

interface QuantityReasonProps {
  reorderPoint: number;
  replenishmentTarget: number | null;
  available: number;
  incoming: number;
  orderedOpen: number;
  recommendedQty: number;
  appliedMoq?: number | null;
  appliedPackSize?: number | null;
}

/**
 * 「なぜこの数量か」の2行表示。補充日数が設定されている行だけ、補充目標の
 * 内訳（発注点 + 補充日数ぶんの想定販売数）を2行目に添える。
 */
function QuantityReason({
  reorderPoint,
  replenishmentTarget,
  available,
  incoming,
  orderedOpen,
  recommendedQty,
  appliedMoq,
  appliedPackSize,
}: QuantityReasonProps) {
  const target = {
    reorder_point: reorderPoint,
    replenishment_target: replenishmentTarget,
  };
  const breakdown = replenishmentTargetBreakdown(target);
  return (
    <BlockStack gap="050">
      <Text as="span" variant="bodySm" tone="subdued">
        {purchaseOrderReason({
          ...target,
          available,
          incoming,
          ordered_open: orderedOpen,
          recommended_qty: recommendedQty,
          applied_moq: appliedMoq,
          applied_pack_size: appliedPackSize,
        })}
      </Text>
      {breakdown && (
        <Text as="span" variant="bodySm" tone="subdued">
          {breakdown}
        </Text>
      )}
    </BlockStack>
  );
}

function NewPurchaseOrderWorkspace() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { shopGeneration } = useVerifiedShopGeneration();
  const [searchParams, setSearchParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  // 配送先（ロケーション）。null = 未選択（バックエンドが主拠点へ解決する。
  // 全拠点合算はもう既定ではない＝配送先 Phase2 ⑤）。
  const requestedLocationId = searchParams.get("location");
  const [locationId, setLocationId] = useState<StockyPublicBigIntId | null>(
    () =>
      isStockyPublicBigIntId(requestedLocationId)
        ? canonicalStockyPublicBigIntId(requestedLocationId)
        : null,
  );
  const [seasonPreview, setSeasonPreview] = useState<SeasonPreview>(null);

  // 🔴 配送先の選択肢は「拠点数の階段」で決まる（旧＝Business の機能ゲート）。
  const locationLimit = useLocationLimit();
  const canMultiLocation =
    locationLimit === undefined ||
    locationLimit.limit === null ||
    locationLimit.limit > 1;

  const locationsQuery = useQuery({
    queryKey: ["po-locations"],
    queryFn: listLocations,
    // /api/locations は Business ゲート（403）。未満では取得しない。
    enabled: canMultiLocation,
  });

  const draftsQuery = useQuery({
    queryKey: ["purchase-order-drafts", offset, locationId, seasonPreview],
    queryFn: () =>
      listPurchaseOrderDrafts(
        PURCHASE_ORDER_PAGE_LIMIT,
        offset,
        locationId,
        seasonPreview,
      ),
    placeholderData: keepPreviousData,
  });

  const suppliersQuery = useQuery({
    queryKey: ["po-suppliers"],
    queryFn: listSuppliers,
  });

  // 設定PATCH。成功時はドラフト一覧を再取得（税区分・仕入先・仕入単価で再集計されるため）。
  const invalidateDrafts = () =>
    queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });

  const invoiceMutation = useMutation({
    mutationFn: (vars: {
      supplierId: StockyPublicBigIntId;
      value: string | null;
    }) => setSupplierInvoiceNo(vars.supplierId, vars.value),
    onSuccess: () => {
      invalidateDrafts();
      setEditingInvoice(false);
    },
  });
  const contactEmailMutation = useMutation({
    mutationFn: (vars: {
      supplierId: StockyPublicBigIntId;
      value: string | null;
    }) => setSupplierContactEmail(vars.supplierId, vars.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["po-suppliers"] });
      setEditingContactEmail(false);
    },
  });
  const [emailPreview, setEmailPreview] =
    useState<PurchaseOrderEmailPreview | null>(null);
  const emailAttempt = useRef<PendingPurchaseOrderEmailAttempt | null>(null);
  const emailSectionRef = useRef<HTMLDivElement | null>(null);
  const emailAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [emailPersistenceError, setEmailPersistenceError] = useState<
    string | null
  >(null);
  const [duplicateSuppressed, setDuplicateSuppressed] = useState<{
    result: PurchaseOrderEmailSendResult;
    preview: PurchaseOrderEmailPreview;
    requestedLocationId: StockyPublicBigIntId | null;
  } | null>(null);
  const [pendingDeliveryOverride, setPendingDeliveryOverride] = useState<{
    priorDeliveryId: StockyPublicBigIntId;
    confirmationToken: "send_new_purchase_order";
  } | null>(null);
  const emailDraftMutation = useMutation({
    mutationFn: (vars: {
      supplierId: StockyPublicBigIntId;
      locationId: StockyPublicBigIntId | null;
      preview: PurchaseOrderEmailPreview;
      attempt: PendingPurchaseOrderEmailAttempt;
    }) => {
      const deliveryOverride =
        vars.attempt.priorDeliveryId !== null &&
        vars.attempt.confirmationToken !== null
          ? {
              priorDeliveryId: vars.attempt.priorDeliveryId,
              confirmationToken: vars.attempt.confirmationToken,
            }
          : null;
      return deliveryOverride === null
        ? emailPurchaseOrderDraft(
            vars.supplierId,
            vars.locationId,
            vars.preview.draft_sha,
            vars.attempt.key,
            vars.preview.purchase_order_note ?? null,
            null,
            vars.preview,
          )
        : emailPurchaseOrderDraft(
            vars.supplierId,
            vars.locationId,
            vars.preview.draft_sha,
            vars.attempt.key,
            vars.preview.purchase_order_note ?? null,
            deliveryOverride,
            vars.preview,
          );
    },
    onSuccess: (result, vars) => {
      clearPurchaseOrderEmailAttempt(vars.attempt);
      emailAttempt.current = null;
      setEmailPreview(null);
      setEmailPersistenceError(null);
      setPendingDeliveryOverride(null);
      setDuplicateSuppressed(
        result.duplicate_suppressed === true &&
          result.replayed_from_delivery_id !== null &&
          result.replayed_from_delivery_id !== undefined
          ? {
              result,
              preview: vars.preview,
              requestedLocationId: vars.locationId,
            }
          : null,
      );
      queryClient.invalidateQueries({
        queryKey: ["purchase-order-email-deliveries"],
      });
    },
    onError: (error, vars) => {
      if (
        error instanceof ApiError &&
        error.code === "recently_delivered" &&
        error.deliveryId !== null &&
        error.confirmationToken === "send_new_purchase_order"
      ) {
        try {
          emailAttempt.current = addPurchaseOrderEmailDeliveryOverride(
            vars.attempt,
            error.deliveryId,
            error.confirmationToken,
          );
          setPendingDeliveryOverride({
            priorDeliveryId: error.deliveryId,
            confirmationToken: error.confirmationToken,
          });
          setEmailPersistenceError(null);
        } catch {
          setEmailPersistenceError(
            t(
              "前回送信の確認情報をこのタブに安全に保存できなかったため、別注文のメールは送信できません。ブラウザのストレージ設定を確認してください。",
            ),
          );
        }
        setEmailPreview(null);
      } else if (isDefinitiveEmailFailure(error)) {
        clearPurchaseOrderEmailAttempt(vars.attempt);
        emailAttempt.current = null;
        setEmailPreview(null);
        setPendingDeliveryOverride(null);
      } else if (
        error instanceof ApiError &&
        [
          "delivery_unknown",
          "delivery_unresolved",
          "idempotency_key_reused",
        ].includes(error.code ?? "")
      ) {
        // These server outcomes are review-only. Persist that instruction so a
        // reload cannot turn the old operation into a provider retry.
        try {
          emailAttempt.current = requirePurchaseOrderEmailReconciliation(
            vars.attempt,
            error.deliveryId,
          );
        } catch {
          emailAttempt.current = {
            ...vars.attempt,
            disposition: "reconciliation_required",
            deliveryId: error.deliveryId,
          };
        }
        setEmailPreview(null);
        setPendingDeliveryOverride(null);
      }
      queryClient.invalidateQueries({
        queryKey: ["purchase-order-email-deliveries"],
      });
    },
  });
  const emailPreviewMutation = useMutation({
    mutationFn: (vars: {
      supplierId: StockyPublicBigIntId;
      locationId: StockyPublicBigIntId | null;
    }) => getPurchaseOrderEmailPreview(vars.supplierId, vars.locationId),
    onSuccess: (preview) => {
      emailDraftMutation.reset();
      setEmailPersistenceError(null);
      setDuplicateSuppressed(null);
      setEmailPreview(preview);
    },
  });
  const taxMutation = useMutation({
    mutationFn: (vars: {
      productId: StockyPublicBigIntId;
      taxRateId: StockyPublicBigIntId | null;
    }) => setProductTaxCategory(vars.productId, vars.taxRateId),
    onSuccess: invalidateDrafts,
  });
  const supplierAssignMutation = useMutation({
    mutationFn: (vars: {
      variantId: StockyPublicBigIntId;
      supplierId: StockyPublicBigIntId;
    }) => assignVariantSupplier(vars.variantId, vars.supplierId),
    onSuccess: () => {
      invalidateDrafts();
      queryClient.invalidateQueries({ queryKey: ["po-suppliers"] });
    },
  });
  // 仕入単価(cost_override)の手入力。成功時は該当行の入力バッファを破棄し、
  // 再取得後の unit_price（永続値）を素直に表示させる。
  const costMutation = useMutation({
    mutationFn: (vars: {
      variantId: StockyPublicBigIntId;
      cost: number | null;
    }) => setVariantCost(vars.variantId, vars.cost),
    onSuccess: (_data, vars) => {
      invalidateDrafts();
      setCostInputs((prev) => {
        const next = { ...prev };
        delete next[vars.variantId];
        return next;
      });
    },
  });
  // 仕入単価の一括保存。行ごとの onBlur コミット（commitCost）とは別に、入力済みの
  // 単価バッファをまとめて1回の PATCH で永続化する（期首の一斉改定・Stocky 移行後の
  // 原価入力を、行ごとに保存し直さずに済ませる導線）。仕分け規則は commitCost と
  // 揃える（collectBulkCostEdits が単発版と 1:1 の規則で判定する）。
  const bulkCostMutation = useMutation({
    mutationFn: (edits: BulkCostEdits) => setVariantCosts(edits.items),
    onSuccess: (_data, edits) => {
      invalidateDrafts();
      // 保存済み(items)＋現在値と同値だった(unchanged)行のバッファを破棄し、
      // 再取得後の unit_price をそのまま表示させる（無効値のバッファは残す）。
      const clearedIds = [
        ...edits.items.map((i) => i.variantId),
        ...edits.unchangedVariantIds,
      ];
      setCostInputs((prev) => {
        const next = { ...prev };
        for (const id of clearedIds) delete next[id];
        return next;
      });
    },
  });

  // サプライヤー 登録番号のインライン編集。
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [invoiceInput, setInvoiceInput] = useState("");
  const [editingContactEmail, setEditingContactEmail] = useState(false);
  const [contactEmailInput, setContactEmailInput] = useState("");

  // 一時編集（この注文書のみ・永続しない）: 明細の数量/除外、ヘッダ4項目、
  // 仕入単価の入力バッファ（コミット前の文字列）。
  const [edits, setEdits] = useState<DraftEdits>({});
  const [header, setHeader] = useState(() => emptyHeader(todayIso()));
  const headerEdited = useRef(false);
  const [costInputs, setCostInputs] = useState<Record<string, string>>({});
  // 直近の Shopify注文書用CSV ダウンロードで除外された明細数（0 = バナー非表示）。
  const [shopifyCsvSkippedCount, setShopifyCsvSkippedCount] = useState(0);
  const [shopifyCsvDownloadedFilename, setShopifyCsvDownloadedFilename] =
    useState<string | null>(null);
  // バーコードラベルの結果報告。出た枚数・バーコード未設定でスキップした商品数・
  // JAN として描けずコード文字列だけにした商品数を、印刷のたびに知らせる
  // （黙って減らすと「押したのに全部出ない」が原因不明になる）。
  const [labelResult, setLabelResult] = useState<BarcodeLabelReport | null>(
    null,
  );
  // 発注数量の手入力バッファ。手動注文書（Free）で「空欄」を表現するため、
  // 数値の edits とは別に生の入力文字列を保持する（仕入単価の costInputs と同型）。
  const [qtyInputs, setQtyInputs] = useState<Record<string, string>>({});
  const [addedLines, setAddedLines] = useState<PurchaseOrderLine[]>([]);
  const [productSearch, setProductSearch] = useState("");
  // 明細の並び。画面・PDF・CSV・ラベル・確定時の保存まで通す（下の selectedWithAdded）。
  const [sortKey, setSortKey] = useState<LineSortKey>("default");
  // 実際に問い合わせる検索語。打鍵ごとに queryKey を変えると毎打鍵で新規クエリ＝
  // 候補が空になり Select も disabled になって「検索が効かない」ように見えるため、
  // デバウンス後の値だけをキーにする（SupplierSetupCard と同じ方式）。
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [productToAdd, setProductToAdd] = useState("");

  // 発注元（自社プロフィール）を発注書PDF/CSVに差し込むため取得。
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const companyProfile = meQuery.data?.company_profile;

  // 登録番号の「国の概念」（Step 3）。JP＝適格請求書（T+13 検証・未登録警告・経過措置）、
  // GB/DE/FR/AU/CA＝国の呼び名でラベルのみ、US・カタログ外＝null（UI・帳票行を出さない）。
  // country_code の欠落（未同期・旧API）は JP 扱い＝既存の全店が日本なので安全側。
  const registration = registrationConcept(meQuery.data?.country_code);
  const isJapanStore = registration?.isJapanInvoiceSystem === true;

  // 配送先の出し分け: 単一ロケ店はセレクタを出さずその1拠点に確定、
  // 複数ロケ店だけ選択可（Business ゲート）。/api/me の要約から判定する。
  const locationCount = meQuery.data?.locations?.count ?? 0;
  const soleLocationName = meQuery.data?.locations?.sole_name ?? null;
  const primaryLocationId =
    meQuery.data?.locations?.primary_location_id ?? null;
  const primaryLocationName = meQuery.data?.locations?.primary_name ?? null;
  const isMultiLocation = locationCount >= 2;

  const drafts = draftsQuery.data?.drafts ?? [];
  const total = draftsQuery.data?.total ?? 0;
  // Free（<Starter）は手動注文書＝予測数量が空欄で届く。数量を手入力する UI に切り替える。
  // 自動注文書（Starter+）は従来どおり推奨数量を初期表示する。
  const autoQuantity = draftsQuery.data?.auto_quantity ?? true;
  const seasonalAdjustment = draftsQuery.data?.seasonal_adjustment ?? null;
  // プレビューできる季節イベント＝店の所在国の暦。国ごとに違うので backend が配る。
  const seasonPreviews = draftsQuery.data?.season_previews ?? [];
  const seasonNames = seasonPreviews
    .map((option) => option.name)
    .join(currentLocale() === "ja" ? "・" : ", ");
  const hasNextPage = offset + PURCHASE_ORDER_PAGE_LIMIT < total;
  const hasPreviousPage = offset > 0;
  const [supplierId, setSupplierId] = useState<StockyPublicBigIntId | null>(
    null,
  );

  // ダッシュボードのアラートから「◯◯に発注する」で来たときの着地先。
  // 🔴 ドラフトが揃うまで判定しない＝auto では「発注点を割った商品を持つ仕入先」しか
  // ドラフトに現れないので、在庫が戻った後の古いアラートから来ると対象が居ない。
  // そのとき黙って別の仕入先を開くと**別会社の発注書を編集させる**ので、理由を出す。
  // 🔴 使い終えたら URL から消す＝残すと、画面内で仕入先を切り替えるたびにここへ
  // 引き戻される。
  const requestedSupplierValue = searchParams.get("supplier");
  const requestedSupplierId = isStockyPublicBigIntId(requestedSupplierValue)
    ? canonicalStockyPublicBigIntId(requestedSupplierValue)
    : null;
  // null＝そもそも要求が無い。オブジェクト＝要求されたが居なかった（名前は引ければ入る）。
  // 🔴 名前の有無で「出す/出さない」を切り替えない＝仕入先一覧が引けなかっただけで
  //    警告ごと消えると、別の仕入先を黙って開くのと同じことになる。
  const [missingSupplier, setMissingSupplier] = useState<{
    name: string | null;
  } | null>(null);
  useEffect(() => {
    if (requestedSupplierId === null) return;
    // 取得中・取得失敗では判定できない（空配列を「居ない」と読むと嘘になる）。
    if (draftsQuery.isLoading || draftsQuery.isError) return;
    const requestedDraft = drafts.find(
      (draft) =>
        canonicalStockyPublicBigIntId(draft.supplier_id) ===
        requestedSupplierId,
    );
    if (requestedDraft !== undefined) {
      setSupplierId(requestedDraft.supplier_id);
    } else {
      setMissingSupplier({
        name:
          (suppliersQuery.data ?? []).find(
            (supplier) =>
              canonicalStockyPublicBigIntId(supplier.id) ===
              requestedSupplierId,
          )?.name ?? null,
      });
    }
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("supplier");
        return next;
      },
      { replace: true },
    );
  }, [
    requestedSupplierId,
    draftsQuery.isLoading,
    draftsQuery.isError,
    drafts,
    suppliersQuery.data,
    setSearchParams,
  ]);

  // Default to the first supplier once drafts load; keep selection if still present.
  useEffect(() => {
    if (drafts.length === 0) {
      setSupplierId(null);
      return;
    }
    setSupplierId((current) =>
      current !== null &&
      drafts.some(
        (draft) =>
          canonicalStockyPublicBigIntId(draft.supplier_id) ===
          canonicalStockyPublicBigIntId(current),
      )
        ? current
        : drafts[0].supplier_id,
    );
  }, [drafts]);

  const selected: PurchaseOrderDraft | undefined = drafts.find(
    (draft) =>
      supplierId !== null &&
      canonicalStockyPublicBigIntId(draft.supplier_id) ===
        canonicalStockyPublicBigIntId(supplierId),
  );
  useEffect(() => {
    const id = setTimeout(
      () => setProductSearchTerm(productSearch),
      PRODUCT_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [productSearch]);

  const orderableVariantsQuery = useQuery({
    queryKey: ["po-orderable-variants", productSearchTerm, locationId],
    queryFn: () => listOrderableVariants(productSearchTerm, locationId),
    enabled: Boolean(selected),
    // 検索語を変えている間も直前の候補を残す（毎回空にしない）。
    placeholderData: keepPreviousData,
  });

  // サプライヤーを切り替えたら登録番号エディタを閉じ、選択中の値で初期化する。
  useEffect(() => {
    setEditingInvoice(false);
    setInvoiceInput(selected?.invoice_registration_no ?? "");
  }, [selected?.supplier_id, selected?.invoice_registration_no]);

  const selectedSupplier = (suppliersQuery.data ?? []).find(
    (supplier) =>
      selected !== undefined &&
      canonicalStockyPublicBigIntId(supplier.id) ===
        canonicalStockyPublicBigIntId(selected.supplier_id),
  );
  const stockySupplierNotesQuery = useQuery({
    queryKey: ["po-supplier-stocky-notes", selectedSupplier?.id ?? null],
    queryFn: () => getSupplierStockyNotes(selectedSupplier!.id),
    enabled: selectedSupplier !== undefined,
  });
  const stockyPrivateNote =
    stockySupplierNotesQuery.data?.supplier_private_note ?? null;
  const stockyPoNoteTemplate =
    stockySupplierNotesQuery.data?.supplier_po_note_template ?? null;
  // Operational PO notes are capped at 2,000 characters.  Never truncate a
  // retained Stocky transcription silently; show it internally and ask the
  // merchant to shorten it explicitly instead.
  const stockyPoNoteDefault =
    stockyPoNoteTemplate !== null && stockyPoNoteTemplate.text.length <= 2000
      ? stockyPoNoteTemplate.text
      : "";
  const selectedSupplierEmailAttempt =
    selectedSupplier === undefined
      ? null
      : findPurchaseOrderEmailAttemptForSupplier(
          shopGeneration,
          selectedSupplier.id,
        );
  const recentPriorDeliveryId =
    selectedSupplierEmailAttempt?.priorDeliveryId ??
    duplicateSuppressed?.result.replayed_from_delivery_id ??
    null;
  const priorDeliveryQuery = useQuery({
    queryKey:
      recentPriorDeliveryId === null
        ? [...purchaseOrderEmailReconciliationKeys.all, "prior", "none"]
        : purchaseOrderEmailReconciliationKeys.detail(recentPriorDeliveryId),
    queryFn: () => getPurchaseOrderEmailDelivery(recentPriorDeliveryId!),
    enabled: recentPriorDeliveryId !== null,
  });
  useEffect(() => {
    setEditingContactEmail(false);
    setContactEmailInput(selectedSupplier?.contact_email ?? "");
    const pending =
      selectedSupplier === undefined
        ? null
        : findPurchaseOrderEmailAttemptForSupplier(
            shopGeneration,
            selectedSupplier.id,
          );
    emailAttempt.current = pending;
    if (pending !== null && pending.requestedLocationId !== locationId) {
      setLocationId(pending.requestedLocationId);
    }
    setEmailPreview(
      pending?.disposition === "retryable" ? pending.preview : null,
    );
    setPendingDeliveryOverride(
      pending?.priorDeliveryId !== null &&
        pending?.priorDeliveryId !== undefined &&
        pending.confirmationToken !== null
        ? {
            priorDeliveryId: pending.priorDeliveryId,
            confirmationToken: pending.confirmationToken,
          }
        : null,
    );
    setEmailPersistenceError(null);
    setDuplicateSuppressed(null);
    emailPreviewMutation.reset();
    emailDraftMutation.reset();
  }, [
    locationId,
    selectedSupplier?.contact_email,
    selectedSupplier?.id,
    shopGeneration,
  ]);

  const confirmPurchaseOrderEmail = () => {
    if (emailPreview === null || selected === undefined) return;
    let attempt: PendingPurchaseOrderEmailAttempt;
    try {
      attempt = persistPurchaseOrderEmailAttempt({
        shopGeneration,
        supplierId: selected.supplier_id,
        requestedLocationId: locationId,
        preview: emailPreview,
        previous: emailAttempt.current,
        priorDeliveryId: pendingDeliveryOverride?.priorDeliveryId,
        confirmationToken: pendingDeliveryOverride?.confirmationToken,
      });
    } catch (error) {
      if (
        error instanceof PurchaseOrderEmailAttemptStorageError ||
        error instanceof PurchaseOrderEmailAttemptConflictError
      ) {
        setEmailPersistenceError(
          t(
            "送信内容と操作キーをこのタブに安全に保存できなかったため、メールは送信していません。ブラウザのストレージ設定を確認して再試行してください。",
          ),
        );
        return;
      }
      throw error;
    }
    if (attempt.disposition !== "retryable") {
      setEmailPreview(null);
      return;
    }
    emailAttempt.current = attempt;
    setEmailPersistenceError(null);
    emailDraftMutation.mutate({
      supplierId: attempt.supplierId,
      locationId: attempt.requestedLocationId,
      preview: attempt.preview,
      attempt,
    });
  };

  const addPurchaseOrderEmailAttachments = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (emailPreview === null || files.length === 0) return;
    if (emailAttempt.current !== null) {
      setEmailPersistenceError(
        t("送信受付後のメールは編集・添付変更できません。結果を確認してください。"),
      );
      return;
    }
    try {
      const additions = await Promise.all(files.map(readPurchaseOrderEmailAttachment));
      const existing = emailPreview.attachments ?? [];
      const combined = [...existing, ...additions];
      if (combined.length > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxCount) {
        throw new Error(t("添付は 3 件までです。"));
      }
      const decodedTotal = combined.reduce(
        (total, attachment) => total + Math.floor((attachment.content.length * 3) / 4),
        0,
      );
      if (decodedTotal > PURCHASE_ORDER_EMAIL_ATTACHMENT_LIMITS.maxTotalBytes) {
        throw new Error(t("添付ファイル合計は 2 MiB 以下にしてください。"));
      }
      if (
        new Set(combined.map((attachment) => attachment.filename.toLocaleLowerCase())).size !==
        combined.length
      ) {
        throw new Error(t("同じ名前の添付ファイルは1件だけにしてください。"));
      }
      setEmailPreview({ ...emailPreview, attachments: combined });
      setEmailPersistenceError(null);
    } catch (error) {
      setEmailPersistenceError(
        error instanceof Error ? error.message : t("添付ファイルを追加できませんでした。"),
      );
    }
  };

  const removePurchaseOrderEmailAttachment = (filename: string) => {
    if (emailPreview === null || emailAttempt.current !== null) return;
    setEmailPreview({
      ...emailPreview,
      attachments: (emailPreview.attachments ?? []).filter(
        (attachment) => attachment.filename !== filename,
      ),
    });
  };

  const defaultHeader = emptyHeader(todayIso(), stockyPoNoteDefault);
  const hasUnsavedDraftChanges =
    Object.keys(edits).length > 0 ||
    Object.keys(costInputs).length > 0 ||
    Object.keys(qtyInputs).length > 0 ||
    addedLines.length > 0 ||
    header.issueDate !== defaultHeader.issueDate ||
    header.poNumber !== defaultHeader.poNumber ||
    header.desiredDeliveryDate !== defaultHeader.desiredDeliveryDate ||
    header.note !== defaultHeader.note ||
    (editingInvoice &&
      invoiceInput !== (selected?.invoice_registration_no ?? "")) ||
    (editingContactEmail &&
      contactEmailInput !== (selectedSupplier?.contact_email ?? ""));

  // 仕入先・配送先・季節プレビュー等はドラフトの母集団を取り直す操作。一時編集を
  // 黙って消さず、キャンセルなら現在の入力をそのまま保持する。
  const pendingContextChange = useRef<(() => void) | null>(null);
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const requestContextChange = useCallback(
    (action: () => void) => {
      if (!hasUnsavedDraftChanges) {
        action();
        return;
      }
      pendingContextChange.current = action;
      setDiscardConfirmationOpen(true);
    },
    [hasUnsavedDraftChanges],
  );

  const keepEditing = () => {
    pendingContextChange.current = null;
    setDiscardConfirmationOpen(false);
  };
  const discardAndContinue = () => {
    const action = pendingContextChange.current;
    pendingContextChange.current = null;
    setDiscardConfirmationOpen(false);
    action?.();
  };

  useEffect(() => {
    if (!hasUnsavedDraftChanges) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [hasUnsavedDraftChanges]);

  const resetTemporaryDraftChanges = () => {
    setEdits({});
    setCostInputs({});
    setQtyInputs({});
    setAddedLines([]);
    setProductSearch("");
    setProductSearchTerm("");
    setProductToAdd("");
    headerEdited.current = false;
    setHeader(emptyHeader(todayIso(), stockyPoNoteDefault));
    setEditingInvoice(false);
    setInvoiceInput(selected?.invoice_registration_no ?? "");
    setEditingContactEmail(false);
    setContactEmailInput(selectedSupplier?.contact_email ?? "");
  };

  // サプライヤーを切り替えたら一時編集（数量/除外/ヘッダ/仕入単価バッファ）を破棄する。
  // 仕入単価コミット後の再取得では supplier_id は不変なので編集は保持される。
  useEffect(() => {
    setEdits({});
    setCostInputs({});
    setQtyInputs({});
    setAddedLines([]);
    setProductSearch("");
    setProductSearchTerm("");
    setProductToAdd("");
    headerEdited.current = false;
    setHeader(emptyHeader(todayIso()));
  }, [selected?.supplier_id]);

  // The note query resolves after the supplier/draft query.  Apply a verified
  // template only while the merchant has not edited this header; a late
  // response must never overwrite typed text.
  useEffect(() => {
    if (headerEdited.current) return;
    setHeader((current) => ({ ...current, note: stockyPoNoteDefault }));
  }, [selected?.supplier_id, stockyPoNoteDefault]);

  // JP＝T+13桁を検証。非 JP＝形式を強制しない（列幅のみ・backend と同じ規則）。
  const invoiceError = !editingInvoice
    ? undefined
    : isJapanStore
      ? !isValidInvoiceNo(invoiceInput)
        ? invoiceNoError()
        : undefined
      : !isValidGenericRegistrationNo(invoiceInput)
        ? genericRegistrationNoError()
        : undefined;

  const supplierOptions = (suppliersQuery.data ?? []).map((s) => ({
    label: s.name,
    value: String(s.id),
  }));
  // 末尾に「＋ 新規登録」＝割り当てたい仕入先が無いと気づいた場で登録へ飛べる。
  const supplierChangeOptions = [
    ...supplierOptions,
    { label: t("＋ 新規仕入先を登録…"), value: NEW_SUPPLIER_OPTION_VALUE },
  ];

  // 配送先ピルの選択肢。全拠点合算はもう選択肢に無い（配送先 Phase2 ⑤）。
  // Business は /api/locations の全件（主拠点含む）、未満は主拠点1件だけを
  // 合成して出す（/api/locations 自体が 403 で引けないため）。
  const locationOptions = canMultiLocation
    ? toLocationSelectOptions(locationsQuery.data ?? [])
    : primaryLocationId !== null && primaryLocationName !== null
      ? [{ label: primaryLocationName, value: String(primaryLocationId) }]
      : [];

  // 発注書に印字する実配送先。単一ロケ=その拠点、複数ロケ=選択拠点
  // （未選択なら主拠点＝実際にバックエンドが解決する拠点と一致させる）。
  const destinationName: string | undefined = isMultiLocation
    ? locationId !== null
      ? locationOptions.find((o) => o.value === String(locationId))?.label
      : (primaryLocationName ?? undefined)
    : (soleLocationName ?? undefined);

  // 一時編集を適用した実効ドラフト（集計カード・PDF・CSV 用）。
  //
  // 🔑 並べ替えはここ1箇所で効かせる＝`applyDraftEdits` は明細の順序を保つので、
  // 画面の表・PDF・CSV・バーコードラベル・確定時の保存まで**同じ並び**になる。
  // 仕入先へ送る書類と手元の画面が違う並びだと、請求書との突合が結局できない。
  //
  // ⚠️ 数量順は**ドラフト自身の推奨数量**で並べる（手入力中の値では並べない）＝
  // 打鍵のたびに行が動くと、入力中の欄が指の下から逃げる。
  const selectedWithAdded = selected
    ? {
        ...selected,
        lines: sortLines(
          [...selected.lines, ...addedLines],
          sortKey,
          (line) => ({
            sku: line.sku,
            title: line.title,
            qty: line.recommended_qty ?? 0,
          }),
          currentLocale(),
        ),
      }
    : undefined;
  // 税率の選択肢は backend 配信（`tax_rates`）。未設定（＝店の既定に従う）も選べる。
  // 🔴 税率行が1つも無い店（US・カタログ外の国＝Step 4）は税列・税集計ごと出さない
  //    ＝空の disabled セレクタを並べない（「税の行が存在しない」が正しい書類の形）。
  const hasTaxRates = (draftsQuery.data?.tax_rates ?? []).length > 0;
  const taxRateOptions = [
    ...(draftsQuery.data?.tax_rates ?? []).map((rate) => ({
      label: rate.name,
      value: String(rate.id),
    })),
    { label: t("店の既定"), value: "" },
  ];
  const effective = selectedWithAdded
    ? applyDraftEdits(selectedWithAdded, edits)
    : undefined;
  // 画面の金額は【選択中の発注書の通貨】で出す＝PDF・CSV・仕入先メールと同じ1つの
  // 値から作る（`/api/me` からも引けるが、出力と画面がずれる余地を残さない）。
  const yen = (amount: string | number) =>
    money(amount, effective?.currency ?? DEFAULT_CURRENCY);
  // 単価入力の接頭辞。`money()` は桁区切りまで付けるので、記号だけを 0 から取り出す。
  const currencyPrefix = currencySymbol(
    effective?.currency ?? DEFAULT_CURRENCY,
  );

  const existingVariantIds = new Set(
    selectedWithAdded?.lines.map((line) =>
      canonicalStockyPublicBigIntId(line.variant_id),
    ) ?? [],
  );
  const productOptions = (orderableVariantsQuery.data ?? [])
    .filter(
      (line) =>
        !existingVariantIds.has(canonicalStockyPublicBigIntId(line.variant_id)),
    )
    .map((line) => ({
      label: `${line.abc_rank ? `[${line.abc_rank}] ` : ""}${line.title ?? t("商品名なし")}${line.sku ? `（${line.sku}）` : ""}`,
      value: String(line.variant_id),
    }));

  // 検索語を変えると、選択済みの商品が候補から外れることがある。その値を残したままだと
  // 「追加」を押しても該当行が見つからず何も起きない（死にボタン）ので、候補に無い選択は
  // 未選択として扱う。
  const selectedProductValue = productOptions.some(
    (option) => option.value === productToAdd,
  )
    ? productToAdd
    : "";

  // 候補ボタンは検索語があるときだけ出す（空欄でカタログ全件を並べない）。
  const hasProductSearchTerm = productSearchTerm.trim() !== "";
  const visibleProductOptions = productOptions.slice(
    0,
    PRODUCT_SEARCH_RESULT_LIMIT,
  );
  const hiddenProductCount =
    productOptions.length - visibleProductOptions.length;

  const addSelectedProduct = () => {
    const line = orderableVariantsQuery.data?.find(
      (candidate) =>
        canonicalStockyPublicBigIntId(candidate.variant_id) ===
        selectedProductValue,
    );
    if (
      !line ||
      existingVariantIds.has(canonicalStockyPublicBigIntId(line.variant_id))
    )
      return;
    setAddedLines((current) => [...current, line]);
    setQtyInputs((current) => ({ ...current, [line.variant_id]: "1" }));
    updateEdit(line.variant_id, { quantity: 1 });
    setProductToAdd("");
  };

  // 貼り付けプレビューからのまとめて追加。意味論は addSelectedProduct と同じ＝
  // この注文書だけの一時明細（仕入先設定・次回以降の発注書には影響しない）。
  // 数量はプレビューで確定済みなので qtyInputs と edits を同時に初期化する。
  const addPastedProducts = (items: PastedProductAdd[]) => {
    const fresh = items.filter(
      ({ line }) =>
        !existingVariantIds.has(canonicalStockyPublicBigIntId(line.variant_id)),
    );
    if (fresh.length === 0) return;
    setAddedLines((current) => [...current, ...fresh.map(({ line }) => line)]);
    setQtyInputs((current) => ({
      ...current,
      ...Object.fromEntries(
        fresh.map(({ line, quantity }) => [line.variant_id, String(quantity)]),
      ),
    }));
    setEdits((prev) => ({
      ...prev,
      ...Object.fromEntries(
        fresh.map(({ line, quantity }) => [
          line.variant_id,
          { ...prev[line.variant_id], quantity },
        ]),
      ),
    }));
  };

  // 未登録（免税事業者等）サプライヤーは経過措置で支払消費税の一部のみ控除可能。
  // 控除割合は注文書の発行日を基準に判定する。
  // 🔴 適格請求書の経過措置は**日本の制度**＝JP の店だけに出す。国を見ずに
  // 「登録番号が無い」だけで出すと、英米豪のストアに存在しない制度の控除額を
  // 金額つきで断言することになる（isJapanStore の定義は meQuery 直後）。
  const transitional =
    isJapanStore && effective && !selected?.invoice_registration_no
      ? transitionalSummary(
          effective.tax_groups.reduce((sum, g) => sum + Number(g.tax), 0),
          header.issueDate,
        )
      : undefined;

  const updateEdit = (
    variantId: StockyPublicBigIntId,
    patch: Partial<LineEdit>,
  ) =>
    setEdits((prev) => ({
      ...prev,
      [canonicalStockyPublicBigIntId(variantId)]: {
        ...prev[canonicalStockyPublicBigIntId(variantId)],
        ...patch,
      },
    }));

  const updateHeader = (patch: Partial<PurchaseOrderHeader>) => {
    headerEdited.current = true;
    setHeader((h) => ({ ...h, ...patch }));
  };

  // 配送先切替: 拠点別ドラフトを取り直す。ページと一時編集はリセット。
  const handleLocationChange = (value: string) => {
    if (!isStockyPublicBigIntId(value)) return;
    const nextLocationId = canonicalStockyPublicBigIntId(value);
    if (nextLocationId === locationId) return;
    requestContextChange(() => {
      setLocationId(nextLocationId);
      setOffset(0);
      // 任意追加した明細は追加時点の拠点別在庫・入荷予定を持つ一時データ。
      // 配送先を変えた後まで残すと、旧拠点の在庫根拠でPDF/CSVを作れてしまうため破棄する。
      resetTemporaryDraftChanges();
    });
  };

  // 仕入単価バッファをサーバへコミット。空欄は override クリア、無効値は無視。
  const commitCost = (line: PurchaseOrderLine) => {
    const raw = costInputs[line.variant_id];
    if (raw === undefined) return;
    const trimmed = raw.trim();
    if (trimmed === "") {
      costMutation.mutate({ variantId: line.variant_id, cost: null });
      return;
    }
    const n = Number(trimmed);
    if (Number.isNaN(n) || n < 0) return;
    if (n === Number(line.unit_price)) {
      // 変更なし：バッファを捨ててデフォルト表示へ戻す。
      setCostInputs((prev) => {
        const next = { ...prev };
        delete next[line.variant_id];
        return next;
      });
      return;
    }
    costMutation.mutate({ variantId: line.variant_id, cost: n });
  };

  // costInputs の入力バッファをまとめて1回のPATCHで永続化する。
  const handleBulkSaveCosts = () => {
    if (!selected) return;
    const edits = collectBulkCostEdits(selected.lines, costInputs);
    if (edits.items.length === 0 && edits.unchangedVariantIds.length === 0)
      return;
    bulkCostMutation.mutate(edits);
  };

  const handleDownloadCsv = (draft: EffectivePurchaseOrderDraft) => {
    const csv = buildPurchaseOrderCsv(
      draft,
      companyProfile,
      header,
      destinationName,
      {
        // 経過措置は日本の制度＝JP 以外の店の CSV には出さない（PDF と同じゲート）
        showInvoiceTransitionNote: isJapanStore,
        // 登録番号のラベルも店の国で決める（JP＝適格請求書・GB＝VAT number 等）
        registration,
      },
    );
    downloadTextFile(purchaseOrderCsvFilename(draft, todayIso()), csv);
  };

  // Shopify 純正の「注文書」画面へ取り込むための CSV（SKU/バーコード/数量/原価/税率のフラット表）。
  // 上の発注書CSV/PDF は仕入先向けの正本、こちらは純正 PO への橋渡し専用。
  // 純正は SKU/バーコードで照合するため、どちらも無い明細は CSV に入らない＝
  // 黙って欠けると取り込み後まで気付けないので、除外行数を警告バナーで知らせる。
  const handleDownloadShopifyCsv = (draft: EffectivePurchaseOrderDraft) => {
    setShopifyCsvSkippedCount(countShopifyPoImportSkippedLines(draft));
    const csv = buildShopifyPoImportCsv(draft);
    const filename = shopifyPoImportCsvFilename(draft, todayIso());
    downloadTextFile(filename, csv);
    setShopifyCsvDownloadedFilename(filename);
  };

  const handleDownloadPdf = (draft: EffectivePurchaseOrderDraft) => {
    printHtmlDocument(
      buildPurchaseOrderHtml(
        draft,
        todayIso(),
        companyProfile,
        header,
        destinationName,
        {
          // 経過措置は日本の制度＝JP 以外の店の PDF には出さない（画面のバナーと同じゲート）
          showInvoiceTransitionNote: isJapanStore,
          // 登録番号のラベルも店の国で決める（JP＝適格請求書・GB＝VAT number 等）
          registration,
        },
      ),
    );
  };

  // 入荷した商品に貼るバーコードラベル。枚数は発注数量に合わせる
  // （1個につき1枚＝棚入れ時にそのまま貼れる）。発注書 PDF と同じ印刷経路に相乗り。
  const handlePrintBarcodeLabels = (draft: EffectivePurchaseOrderDraft) => {
    setLabelResult(
      printBarcodeLabels(
        draft.lines.map((line) => ({
          sku: line.sku,
          barcode: line.barcode,
          title: line.title,
          qty: line.recommended_qty,
        })),
        { title: draft.supplier_name },
      ),
    );
  };

  // 未選択(null)なら主拠点を選択中として表示する（バックエンドが実際に解決する拠点と一致）。
  const selectedLocationValue =
    locationId !== null
      ? String(locationId)
      : primaryLocationId !== null
        ? String(primaryLocationId)
        : "";

  // 単一ロケ店は配送先をその拠点に確定表示。複数ロケ店のうち Business だけ
  // 選択セレクタを出す。Business 未満は主拠点固定表示（全ロケーション廃止）。
  const destination: DestinationProp = isMultiLocation
    ? canMultiLocation
      ? {
          mode: "select",
          options: locationOptions,
          value: selectedLocationValue,
          onChange: handleLocationChange,
          // Changing location must not hide an outcome-unknown attempt for the
          // same supplier and mint a new operation key.
          disabled: selectedSupplierEmailAttempt !== null,
        }
      : primaryLocationName
        ? { mode: "fixed", name: primaryLocationName }
        : { mode: "none" }
    : soleLocationName
      ? { mode: "fixed", name: soleLocationName }
      : { mode: "none" };

  const [placedPoNumber, setPlacedPoNumber] = useState<string | null>(null);
  const placeAttempt = useRef<PersistentIdempotencyAttempt | null>(null);
  // 「注文済みとしてマーク」＝Stocky の Mark as ordered。編集適用後の実効ドラフトを
  // 自前DBへ永続化し、未入荷分は次回の発注提案から自動で差し引かれる
  // （Shopify の在庫・転送には書き込まない）。
  const placeMutation = useMutation({
    mutationFn: ({
      input,
      attempt,
    }: {
      input: Parameters<typeof placePurchaseOrder>[0];
      attempt: PersistentIdempotencyAttempt;
    }) => placePurchaseOrder(input, attempt.key),
    onSuccess: (po, variables) => {
      clearPersistentIdempotencyAttempt(
        PLACE_ORDER_ATTEMPT_SCOPE,
        variables.attempt,
      );
      placeAttempt.current = null;
      setPlacedPoNumber(po.po_number);
      queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
    },
    onError: (error, variables) => {
      if (isDefinitivePlaceFailure(error)) {
        clearPersistentIdempotencyAttempt(
          PLACE_ORDER_ATTEMPT_SCOPE,
          variables.attempt,
        );
        placeAttempt.current = null;
      }
    },
  });

  // 押すと即 PO 確定＝以後この数量がドラフトから差し引かれる。取り消しには
  // 「発注済み・入荷」ページでのキャンセルが要るので、登録側にも確認を挟む
  // （キャンセル側にだけ confirm がある非対称を解消）。
  const handlePlaceOrder = useCallback(
    (draft: EffectivePurchaseOrderDraft) => {
      const lines = draft.lines.filter((line) => line.recommended_qty > 0);
      const totalQty = lines.reduce(
        (sum, line) => sum + line.recommended_qty,
        0,
      );
      if (
        window.confirm(
          t("この内容で発注を確定します（{items}品目・合計{qty}点）。", {
            items: lines.length,
            qty: totalQty,
          }) +
            t(
              "以後この数量は、入荷を登録するまで発注提案から差し引かれます。",
            ) +
            t("取り消すには「発注済み・入荷」ページでキャンセルしてください。"),
        )
      ) {
        const input: Parameters<typeof placePurchaseOrder>[0] = {
          supplier_id: draft.supplier_id,
          // The right-rail fields are part of the order the merchant just
          // reviewed.  Keep them in the same idempotency fingerprint and
          // persist them with the placed PO; otherwise PDF/CSV can show a
          // delivery date or supplier note that silently disappears as soon
          // as the order is marked ordered.
          note: header.note.trim() || null,
          expected_at: header.desiredDeliveryDate || null,
          stocky_supplier_po_note_revision_id:
            stockyPoNoteTemplate !== null &&
            header.note === stockyPoNoteTemplate.text
              ? stockyPoNoteTemplate.revision_id
              : null,
          lines: lines.map((line) => ({
            shopify_variant_id: line.shopify_variant_id,
            quantity: line.recommended_qty,
            unit_price: line.unit_price,
            tax_rate_id: line.tax_rate_id,
          })),
        };
        const attempt = persistentIdempotencyAttempt({
          scope: PLACE_ORDER_ATTEMPT_SCOPE,
          shopGeneration,
          previous: placeAttempt.current,
          fingerprint: JSON.stringify(input),
        });
        placeAttempt.current = attempt;
        placeMutation.mutate({ input, attempt });
      }
    },
    [
      header.desiredDeliveryDate,
      header.note,
      placeMutation,
      shopGeneration,
      stockyPoNoteTemplate,
    ],
  );

  const showConfigError =
    invoiceMutation.isError ||
    taxMutation.isError ||
    supplierAssignMutation.isError ||
    costMutation.isError ||
    bulkCostMutation.isError;
  const showSupplierEmailError =
    contactEmailMutation.isError ||
    emailPreviewMutation.isError ||
    emailDraftMutation.isError ||
    emailPersistenceError !== null;
  const supplierEmailError =
    (emailDraftMutation.error as Error | null) ??
    (emailPreviewMutation.error as Error | null) ??
    (contactEmailMutation.error as Error | null) ??
    (emailPersistenceError === null ? null : new Error(emailPersistenceError));
  const supplierEmailApiError =
    supplierEmailError instanceof ApiError ? supplierEmailError : null;
  const supplierEmailNeedsReconciliation =
    [
      "delivery_unknown",
      "delivery_unresolved",
      "idempotency_key_reused",
    ].includes(supplierEmailApiError?.code ?? "") ||
    selectedSupplierEmailAttempt?.disposition === "reconciliation_required";
  const reviewedEmailIsValid =
    emailPreview !== null &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPreview.to_email) &&
    emailPreview.subject.trim().length > 0 &&
    !/[\r\n]/.test(emailPreview.subject) &&
    emailPreview.text.trim().length > 0;
  const reconciliationDeliveryId =
    supplierEmailApiError?.deliveryId ??
    selectedSupplierEmailAttempt?.deliveryId ??
    null;

  const openPurchaseOrderEmailPreview = () => {
    if (!selectedSupplier) return;
    const pending = findPurchaseOrderEmailAttemptForSupplier(
      shopGeneration,
      selectedSupplier.id,
    );
    emailPreviewMutation.reset();
    emailDraftMutation.reset();
    setEmailPersistenceError(null);
    if (pending !== null) {
      emailAttempt.current = pending;
      setPendingDeliveryOverride(
        pending.priorDeliveryId !== null && pending.confirmationToken !== null
          ? {
              priorDeliveryId: pending.priorDeliveryId,
              confirmationToken: pending.confirmationToken,
            }
          : null,
      );
      if (pending.requestedLocationId !== locationId) {
        setLocationId(pending.requestedLocationId);
      }
      if (pending.disposition === "retryable") {
        setEmailPreview(pending.preview);
      }
      return;
    }
    emailPreviewMutation.mutate({
      supplierId: selectedSupplier.id,
      locationId,
    });
  };

  const openPurchaseOrderEmailEntry = () => {
    if (
      selectedSupplierEmailAttempt?.disposition === "reconciliation_required"
    ) {
      navigate(
        selectedSupplierEmailAttempt.deliveryId === null
          ? "/purchase-order-email-reconciliation"
          : `/purchase-order-email-reconciliation/${selectedSupplierEmailAttempt.deliveryId}`,
      );
      return;
    }
    if (!selectedSupplier?.contact_email) {
      setEditingContactEmail(true);
      window.requestAnimationFrame(() =>
        emailSectionRef.current?.scrollIntoView?.({
          behavior: "smooth",
          block: "center",
        }),
      );
      return;
    }
    openPurchaseOrderEmailPreview();
  };

  const beginSeparatePurchaseOrderEmail = () => {
    const pending = selectedSupplier
      ? findPurchaseOrderEmailAttemptForSupplier(
          shopGeneration,
          selectedSupplier.id,
        )
      : null;
    if (
      pending?.disposition === "retryable" &&
      pending?.priorDeliveryId !== null &&
      pending?.priorDeliveryId !== undefined &&
      pending.confirmationToken !== null
    ) {
      emailAttempt.current = pending;
      setPendingDeliveryOverride({
        priorDeliveryId: pending.priorDeliveryId,
        confirmationToken: pending.confirmationToken,
      });
      setEmailPreview(pending.preview);
      return;
    }
    const replayedFrom = duplicateSuppressed?.result.replayed_from_delivery_id;
    if (
      duplicateSuppressed === null ||
      replayedFrom === null ||
      replayedFrom === undefined ||
      duplicateSuppressed.result.confirmation_token !==
        "send_new_purchase_order"
    ) {
      return;
    }
    emailAttempt.current = null;
    setPendingDeliveryOverride({
      priorDeliveryId: replayedFrom,
      confirmationToken: "send_new_purchase_order",
    });
    setEmailPreview(duplicateSuppressed.preview);
  };

  const closePurchaseOrderEmailPreview = () => {
    if (emailDraftMutation.isPending) return;
    setEmailPreview(null);
    if (emailAttempt.current === null) {
      setPendingDeliveryOverride(null);
    }
  };

  const priorDelivery = priorDeliveryQuery.data;
  const priorToEmail = priorDelivery?.to_email ?? null;
  const priorFromEmail = priorDelivery?.from_email ?? null;
  const priorSubject = priorDelivery?.subject ?? null;
  const priorText = priorDelivery?.text ?? null;
  const priorDeliveryIsDisplayable =
    priorDelivery !== undefined &&
    priorToEmail !== null &&
    priorFromEmail !== null &&
    priorSubject !== null &&
    priorText !== null &&
    priorDelivery.delivered_at !== null;
  const isSeparatePurchaseOrderConfirmation = pendingDeliveryOverride !== null;

  return (
    <Page
      title={t("注文書を作成")}
      subtitle={t(
        "販売速度・在庫・入荷予定から発注点を割った商品をサプライヤー別に集計します",
      )}
      /* 発注ループはアプリ内で閉じた（提案→注文書→発注確定→入荷登録）ので、
         この画面の主操作は発注確定。Shopify 純正の「支払い済みとしてマーク」に
         合わせた「〜としてマーク」の言い回し＋濃色の主ボタンにする。
         純正の注文書側で入荷まで管理する店の橋渡し（CSV）は残すが副次操作へ。 */
      primaryAction={
        effective && effective.lines.length > 0
          ? {
              content: t("注文済みとしてマーク"),
              onAction: () => handlePlaceOrder(effective),
              helpText: t(
                "Shopify 純正の注文書で入荷予定（転送）を作った分をここでも登録すると、同じ発注が二重に差し引かれて次回の提案数量が少なめに出ます。どちらか一方で管理してください",
              ),
              loading: placeMutation.isPending,
              disabled:
                placeMutation.isPending ||
                !effective.lines.some((line) => line.recommended_qty > 0),
            }
          : undefined
      }
      secondaryActions={
        effective && effective.lines.length > 0
          ? [
              {
                content:
                  selectedSupplierEmailAttempt?.disposition ===
                  "reconciliation_required"
                    ? t("発注メール（要確認）")
                    : selectedSupplier?.contact_email
                      ? t("発注メール")
                      : t("仕入先メールを登録"),
                onAction: openPurchaseOrderEmailEntry,
                helpText: !autoQuantity
                  ? t("発注書のメール送信は Starter プラン以上で利用できます。")
                  : selectedSupplier?.contact_email
                    ? t("宛先・件名・本文を確認してから送信します。")
                    : t(
                        "仕入先のメールアドレスを登録すると、発注内容を確認して送信できます。",
                      ),
                disabled:
                  !autoQuantity ||
                  selectedSupplier === undefined ||
                  emailPreviewMutation.isPending ||
                  emailDraftMutation.isPending ||
                  duplicateSuppressed !== null,
              },
              {
                content: t("CSVダウンロード"),
                onAction: () => handleDownloadCsv(effective),
                helpText: t(
                  "仕入先へ送る発注書の正本（明細・税区分・登録番号入り）",
                ),
              },
              {
                content: t("PDF（印刷・保存）"),
                onAction: () => handleDownloadPdf(effective),
              },
              {
                content: t("バーコードラベル（印刷）"),
                onAction: () => handlePrintBarcodeLabels(effective),
                helpText: t(
                  "入荷した商品に貼るJANラベル。発注数量ぶんの枚数を出します（A4・3列）",
                ),
              },
              {
                content: t("Shopify注文書用CSV"),
                onAction: () => handleDownloadShopifyCsv(effective),
                helpText: t(
                  "Shopify 純正の「注文書」画面で入荷・受領まで管理する場合の取込形式（SKU・数量・原価・税率／商品一括登録・在庫のインポート欄では使えません）",
                ),
              },
            ]
          : undefined
      }
    >
      <PurchaseOrderModeSwitch
        active="new"
        confirmLeave={() =>
          !hasUnsavedDraftChanges ||
          window.confirm(t("未保存の編集を破棄して移動しますか？"))
        }
      />
      <Modal
        open={discardConfirmationOpen}
        onClose={keepEditing}
        title={t("未保存の編集を破棄しますか？")}
        primaryAction={{
          content: t("編集を破棄して切り替える"),
          destructive: true,
          onAction: discardAndContinue,
        }}
        secondaryActions={[
          { content: t("編集を続ける"), onAction: keepEditing },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            {t(
              "数量、除外、追加明細、発注書情報など、この注文書だけの未保存編集があります。キャンセルすると入力を保持したまま元の注文書へ戻ります。",
            )}
          </Text>
        </Modal.Section>
      </Modal>
      <Modal
        open={emailPreview !== null}
        onClose={closePurchaseOrderEmailPreview}
        title={
          isSeparatePurchaseOrderConfirmation
            ? t("別の新規注文として送信する前に確認")
            : t("送信する発注書メールを確認")
        }
        primaryAction={{
          content: isSeparatePurchaseOrderConfirmation
            ? emailAttempt.current === null
              ? t("別の新規注文として送信")
              : t("同じ操作キーで別注文送信を再開")
            : t("この内容で送信"),
          onAction: confirmPurchaseOrderEmail,
          loading: emailDraftMutation.isPending,
          disabled:
            emailDraftMutation.isPending ||
            !reviewedEmailIsValid ||
            (isSeparatePurchaseOrderConfirmation &&
              !priorDeliveryIsDisplayable),
        }}
        secondaryActions={[
          {
            content:
              emailAttempt.current === null ? t("キャンセル") : t("後で確認"),
            onAction: closePurchaseOrderEmailPreview,
            disabled: emailDraftMutation.isPending,
          },
        ]}
      >
        <Modal.Section>
          {emailPreview !== null && (
            <BlockStack gap="400">
              {isSeparatePurchaseOrderConfirmation && (
                <Banner
                  tone="critical"
                  title={t("この仕入先には最近送信済みの発注書があります")}
                >
                  {t(
                    "以下の前回メールと今回のメールが別の注文であることを確認してください。同じ注文なら送信しないでください。",
                  )}
                </Banner>
              )}
              {isSeparatePurchaseOrderConfirmation &&
                (priorDeliveryQuery.isLoading ? (
                  <Card>
                    <SkeletonBodyText lines={6} />
                  </Card>
                ) : !priorDeliveryIsDisplayable ? (
                  <Banner
                    tone="critical"
                    title={t("前回送信したメールの全文を確認できません")}
                    action={{
                      content: t("再試行"),
                      onAction: () => void priorDeliveryQuery.refetch(),
                    }}
                  >
                    {t(
                      "前回の宛先・件名・本文・送信時刻を表示できるまで、別注文として送信できません。",
                    )}
                  </Banner>
                ) : (
                  <Card>
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingMd">
                        {t("前回送信済みのメール")}
                      </Text>
                      <Text as="p">
                        {t("送信日時: {value}", {
                          value: formatDateTime(priorDelivery.delivered_at!),
                        })}
                      </Text>
                      <Text as="p">
                        {t("宛先")}: {priorToEmail}
                      </Text>
                      <Text as="p">
                        {t("差出人")}: {priorFromEmail}
                      </Text>
                      <Text as="p">
                        {t("件名")}: {priorSubject}
                      </Text>
                      <Box
                        background="bg-surface-secondary"
                        borderRadius="300"
                        padding="300"
                      >
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            fontFamily: "inherit",
                          }}
                        >
                          {priorText}
                        </pre>
                      </Box>
                    </BlockStack>
                  </Card>
                ))}
              <Banner tone="warning" title={t("表示中の本文だけを送信します")}>
                {t(
                  "宛先・件名・本文・添付を確認または編集してから送信してください。確認後に在庫や設定が変わった場合は送信せず、最新内容の再確認を求めます。",
                )}
              </Banner>
              {!reviewedEmailIsValid && emailAttempt.current === null && (
                <Banner tone="critical">
                  {t("有効な宛先、改行を含まない件名、空白だけではない本文を入力してください。")}
                </Banner>
              )}
              <TextField
                label={t("宛先")}
                value={emailPreview.to_email}
                type="email"
                autoComplete="email"
                disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                onChange={(to_email) =>
                  setEmailPreview({ ...emailPreview, to_email })
                }
              />
              <BlockStack gap="100">
                <Text as="p" fontWeight="semibold">
                  {t("差出人")}
                </Text>
                <Text as="p">{emailPreview.from_email}</Text>
              </BlockStack>
              <TextField
                label={t("件名")}
                value={emailPreview.subject}
                autoComplete="off"
                disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                onChange={(subject) =>
                  setEmailPreview({ ...emailPreview, subject })
                }
              />
              <TextField
                label={t("本文（商品・数量・金額を含む全文）")}
                value={emailPreview.text}
                multiline={16}
                autoComplete="off"
                disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                onChange={(text) => setEmailPreview({ ...emailPreview, text })}
                helpText={t("本文は省略せず、そのまま送信されます。")}
              />
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  {t("添付ファイル")}
                </Text>
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="p">{t("purchase-order.pdf（発注書PDF）")}</Text>
                  <Button
                    variant="plain"
                    tone={
                      emailPreview.include_purchase_order_pdf !== false
                        ? "critical"
                        : undefined
                    }
                    disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                    onClick={() =>
                      setEmailPreview({
                        ...emailPreview,
                        include_purchase_order_pdf:
                          emailPreview.include_purchase_order_pdf === false,
                      })
                    }
                  >
                    {emailPreview.include_purchase_order_pdf !== false
                      ? t("外す")
                      : t("追加")}
                  </Button>
                </InlineStack>
                {(emailPreview.attachments ?? []).map((attachment) => (
                  <InlineStack
                    key={attachment.filename}
                    align="space-between"
                    blockAlign="center"
                  >
                    <Text as="p">{attachment.filename}</Text>
                    <Button
                      variant="plain"
                      tone="critical"
                      disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                      onClick={() => removePurchaseOrderEmailAttachment(attachment.filename)}
                    >
                      {t("外す")}
                    </Button>
                  </InlineStack>
                ))}
                {emailPreview.include_purchase_order_pdf === false &&
                  (emailPreview.attachments?.length ?? 0) === 0 && (
                    <Text as="p" tone="subdued">
                      {t("添付なしで送信します。")}
                    </Text>
                  )}
                <input
                  ref={emailAttachmentInputRef}
                  type="file"
                  multiple
                  aria-label={t("添付ファイルを選択")}
                  accept={Object.keys(PURCHASE_ORDER_EMAIL_ATTACHMENT_TYPES).join(",")}
                  disabled={emailAttempt.current !== null || emailDraftMutation.isPending}
                  onChange={(event) => void addPurchaseOrderEmailAttachments(event)}
                />
                <Text as="p" tone="subdued">
                  {t("PDF、CSV、Excel、Word、PNG、JPEG。3件まで、1件1 MiB、合計2 MiBまで。")}
                </Text>
              </BlockStack>
              <Banner tone="info">
                {t(
                  "この画面だけの未保存の数量変更・商品追加・除外・発注書情報は、このメールには含まれません。送信受付後に在庫・税率・宛先設定を変更しても、受付済みの本文は変わりません。",
                )}
              </Banner>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
      <Layout>
        {hasUnsavedDraftChanges && (
          <Layout.Section>
            <Banner tone="warning" title={t("未保存の編集があります")}>
              <p>
                {t(
                  "仕入先・配送先・季節プレビューを切り替える前に確認します。",
                )}
              </p>
            </Banner>
          </Layout.Section>
        )}
        {placedPoNumber && (
          <Layout.Section>
            <Banner
              tone="success"
              title={t("発注書 {poNumber} を注文済みにしました", {
                poNumber: placedPoNumber,
              })}
              action={{
                content: t("入荷を記録する"),
                onAction: () => navigate("/placed-orders"),
              }}
              onDismiss={() => setPlacedPoNumber(null)}
            >
              <p>
                {t(
                  "入荷待ちの数量は発注提案から自動で差し引かれます。商品が届いたら「発注済み・入荷」ページで入荷を記録してください。",
                )}
              </p>
            </Banner>
          </Layout.Section>
        )}
        {missingSupplier !== null && (
          <Layout.Section>
            <Banner
              tone="info"
              title={t("「{name}」に今必要な発注はありません", {
                name: missingSupplier.name ?? t("指定された仕入先"),
              })}
              onDismiss={() => setMissingSupplier(null)}
            >
              <p>
                {t(
                  "在庫と入荷予定が発注点を満たしているため、この仕入先の発注書ドラフトは作られていません（アラートを見たあとに入荷・売れ行きが変わった可能性があります）。今すぐ発注する場合は、下の「商品を発注書に追加」から追加できます。",
                )}
              </p>
            </Banner>
          </Layout.Section>
        )}
        {placeMutation.isError && (
          <Layout.Section>
            <Banner tone="critical" title={t("注文済みへの登録に失敗しました")}>
              <p>{(placeMutation.error as Error).message}</p>
            </Banner>
          </Layout.Section>
        )}
        {draftsQuery.isError && (
          <Layout.Section>
            <GatedQueryError
              title={t("注文書の取得に失敗しました")}
              onRetry={() => void draftsQuery.refetch()}
            />
          </Layout.Section>
        )}

        {draftsQuery.isLoading && (
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        )}

        {!draftsQuery.isLoading && !draftsQuery.isError && (
          <Layout.Section>
            <BlockStack gap="400">
              {autoQuantity && (
                <Card>
                  <InlineStack gap="300" blockAlign="end">
                    <Box minWidth="280px">
                      <Select
                        label={t("季節補正プレビュー")}
                        options={[
                          { label: t("実際の日付で計算"), value: "actual" },
                          { label: t("通常期（1.00倍）"), value: "normal" },
                          // 選択肢は店の国の暦（backend が配る）。ここに書き写すと
                          // 国を増やしたときに片方だけ古くなる。
                          ...seasonPreviews.map((option) => ({
                            label: t("{name}（{multiplier}倍）", {
                              name: option.name,
                              multiplier: option.multiplier.toFixed(2),
                            }),
                            value: option.slug,
                          })),
                        ]}
                        value={seasonPreview ?? "actual"}
                        onChange={(value) => {
                          const next = value === "actual" ? null : value;
                          if (next === seasonPreview) return;
                          requestContextChange(() => {
                            setSeasonPreview(next);
                            resetTemporaryDraftChanges();
                          });
                        }}
                      />
                    </Box>
                    {seasonPreview && (
                      <Badge tone="attention">{t("プレビュー中")}</Badge>
                    )}
                  </InlineStack>
                </Card>
              )}
              {autoQuantity && seasonalAdjustment && (
                <Banner
                  tone="info"
                  title={t("{name}の季節補正を{state}", {
                    name: seasonalAdjustment.name,
                    state: seasonPreview ? t("プレビュー中") : t("適用中"),
                  })}
                >
                  <p>
                    {t(
                      "承認済みの{count}商品だけ、直近の販売速度を{multiplier}倍して発注点と推奨数量を計算しています。未承認の商品は1.00倍のままです。",
                      {
                        count: seasonalAdjustment.applied_product_count,
                        multiplier: seasonalAdjustment.multiplier.toFixed(2),
                      },
                    )}
                  </p>
                </Banner>
              )}
              {autoQuantity && !seasonalAdjustment && (
                <Banner
                  tone="info"
                  title={
                    seasonPreview === "normal"
                      ? t("通常期（1.00倍）をプレビュー中")
                      : t("季節補正は現在適用されていません")
                  }
                >
                  <p>
                    {seasonPreviews.length > 0
                      ? t(
                          "このストアの所在国の暦（{names}）の期間には、販売速度を補正して発注点と推奨数量を計算します。通常期は1.00倍です。",
                          { names: seasonNames },
                        )
                      : t(
                          "このストアの所在国には固定カレンダーの季節補正がありません。販売速度をそのまま使って発注点と推奨数量を計算します。",
                        )}
                  </p>
                </Banner>
              )}
              <SupplierDestinationHeader
                supplierOptions={drafts.map((d) => ({
                  label: d.supplier_name,
                  value: String(d.supplier_id),
                }))}
                selectedSupplierId={selected?.supplier_id ?? null}
                onSupplierChange={(nextSupplierId) => {
                  if (nextSupplierId === supplierId) return;
                  requestContextChange(() => {
                    resetTemporaryDraftChanges();
                    setSupplierId(nextSupplierId);
                  });
                }}
                destination={destination}
              />

              {autoQuantity &&
                selected &&
                selectedWithAdded &&
                selectedWithAdded.lines.length === 0 && (
                  <Banner
                    tone="success"
                    title={t("この仕入先に今必要な発注はありません")}
                  >
                    <p>
                      {selected.supplier_name}
                      {t(
                        "の商品は、在庫と入荷予定（発注済みぶん）が発注点を満たしています。在庫が発注点を割るとここに発注書ドラフトが自動で作られます。今すぐ発注したい場合は、下の「商品を発注書に追加」から任意の商品を追加できます。",
                      )}
                    </p>
                  </Banner>
                )}

              {/* 🔴 発注点が未設定の明細は数量0で並ぶ＝見た目は「在庫十分で発注不要」と
                  同じだが意味は逆（アプリが発注要否を判断できていない）。説明が無いと
                  「発注しなくてよい」と読まれるので、件数と行き先を添える。 */}
              {autoQuantity &&
                selectedWithAdded &&
                selectedWithAdded.lines.some((line) => line.is_unmanaged) && (
                  <Banner
                    tone="info"
                    title={t("発注点が未設定の商品が含まれています")}
                  >
                    <p>
                      {t(
                        "発注点が未設定の商品は、発注が必要かどうかをアプリが判断できないため数量を空欄のまま並べています（在庫が足りているという意味ではありません）。数量は手入力できます。発注点を設定すると、その商品は自動提案の対象になります。",
                      )}
                    </p>
                    <Box paddingBlockStart="300">
                      <Button onClick={() => navigate("/reorder-policies")}>
                        {t("発注点を設定する")}
                      </Button>
                    </Box>
                  </Banner>
                )}

              {isMultiLocation && !canMultiLocation && (
                <UpgradeNote feature="inventory_transfer">
                  <p>
                    {t("現在は主拠点（{name}）のみの発注書です。", {
                      name: primaryLocationName ?? t("既定の配送先"),
                    })}
                    {t(
                      "他の拠点も含めるには、拠点数の多いプランへの変更が必要です。",
                    )}
                  </p>
                </UpgradeNote>
              )}

              {/* 仕入先の登録／商品への割当が残っていればその導線、終わっていれば
                  「発注が必要な商品はありません」。空表の理由はこの中で一元化する。 */}
              <SupplierSetupCard hasDrafts={drafts.length > 0} />

              {drafts.length > 0 && (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "「注文済みとしてマーク」を押すと発注が確定し、入荷待ちの数量は次回の発注提案から自動で差し引かれます。入荷の記録は「発注済み・入荷」ページで行います。 Shopify 純正の注文書で入荷・受領まで管理している場合は、「その他の操作」の「Shopify注文書用CSV」で純正へ渡せます（同じ発注を両方に登録すると二重に差し引かれるため、どちらか一方で管理してください）。",
                  )}
                </Text>
              )}

              {showConfigError && (
                <Banner tone="critical" title={t("設定の保存に失敗しました")}>
                  <p>{t("時間をおいて再度お試しください。")}</p>
                </Banner>
              )}

              {shopifyCsvSkippedCount > 0 && (
                <Banner
                  tone="warning"
                  title={t(
                    "{count}行はSKU・バーコード未設定のため、Shopify注文書用CSVに含まれていません",
                    { count: shopifyCsvSkippedCount },
                  )}
                  onDismiss={() => setShopifyCsvSkippedCount(0)}
                >
                  <p>
                    {t(
                      "Shopify 純正の注文書は SKU またはバーコード（JAN）で商品を照合します。該当商品は Shopify の商品情報に SKU かバーコードを設定してから再ダウンロードするか、純正の注文書で手動追加してください。",
                    )}
                  </p>
                </Banner>
              )}

              {shopifyCsvDownloadedFilename !== null && (
                <Banner
                  tone="success"
                  onDismiss={() => setShopifyCsvDownloadedFilename(null)}
                >
                  {t("{filename} のダウンロードを開始しました。", {
                    filename: shopifyCsvDownloadedFilename,
                  })}
                </Banner>
              )}

              <BarcodeLabelResultBanner
                result={labelResult}
                onDismiss={() => setLabelResult(null)}
              />

              {selected && effective && effective.has_estimated_lines && (
                <Banner tone="warning" title={t("仕入単価は目安です")}>
                  <p>
                    {t(
                      "一部の商品はShopifyに仕入原価が未設定のため、販売価格を目安に金額を計算しています（「目安」表示）。仕入単価欄に手入力するか、Shopify商品の原価（原価管理）を設定すると正確な発注金額になります。",
                    )}
                  </p>
                </Banner>
              )}

              {selected && transitional && (
                <Banner
                  tone="warning"
                  title={t(
                    "サプライヤーが未登録（免税事業者等）— 仕入税額控除は経過措置の対象です",
                  )}
                >
                  <p>
                    {t(
                      "このサプライヤーは適格請求書発行事業者として未登録のため、支払う消費税 {total} のうち仕入税額控除できるのは {rate}%（{deductible}）までで、残り {nonDeductible} は控除の対象外です。",
                      {
                        total: yen(transitional.totalTax),
                        rate: transitional.measure.ratePercent,
                        deductible: yen(transitional.split.deductible),
                        nonDeductible: yen(transitional.split.nonDeductible),
                      },
                    )}
                    {transitional.measure.next
                      ? t("{date}以降は控除割合が{rate}%へ引き下げられます。", {
                          date: formatJpDate(
                            transitional.measure.next.startDate,
                          ),
                          rate: transitional.measure.next.ratePercent,
                        })
                      : ""}
                  </p>
                  <p>
                    {t(
                      "控除割合は注文書の発行日（{date}）を基準に判定しています。",
                      {
                        date: formatJpDate(header.issueDate),
                      },
                    )}
                  </p>
                </Banner>
              )}

              {!autoQuantity && drafts.length > 0 && (
                <Banner tone="info" title={t("発注数量を手入力してください")}>
                  <p>
                    {t(
                      "現在のプランでは発注数量の自動提案は含まれません。各商品の発注数量を入力すると、金額・消費税・合計が自動で計算され、そのまま発注書（PDF/CSV）に出力できます。販売速度から発注数量を自動で提案する「自動注文書」は、スタータープラン以上でご利用いただけます。",
                    )}
                  </p>
                </Banner>
              )}

              {selected && effective && (
                <Card>
                  <BlockStack gap="300">
                    <Text as="h2" variant="headingMd">
                      {t("商品を発注書に追加")}
                    </Text>
                    <TextField
                      label={t("商品名またはSKUで検索")}
                      value={productSearch}
                      onChange={setProductSearch}
                      autoComplete="off"
                    />
                    {hasProductSearchTerm && (
                      <BlockStack gap="100">
                        {visibleProductOptions.length === 0 &&
                          !orderableVariantsQuery.isFetching && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              {t("「{term}」に一致する商品はありません。", {
                                term: productSearchTerm,
                              })}
                            </Text>
                          )}
                        {visibleProductOptions.map((option) => (
                          <Button
                            key={option.value}
                            onClick={() => setProductToAdd(option.value)}
                            pressed={option.value === selectedProductValue}
                            fullWidth
                            textAlign="left"
                          >
                            {option.label}
                          </Button>
                        ))}
                        {hiddenProductCount > 0 && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            {t(
                              "ほか {count} 件。検索語を増やすと絞り込めます。",
                              {
                                count: hiddenProductCount,
                              },
                            )}
                          </Text>
                        )}
                      </BlockStack>
                    )}
                    <InlineStack gap="200" blockAlign="end">
                      <Box minWidth="320px">
                        <Select
                          label={t("追加する商品")}
                          options={[
                            { label: t("商品を選択"), value: "" },
                            ...productOptions,
                          ]}
                          value={selectedProductValue}
                          onChange={setProductToAdd}
                          disabled={orderableVariantsQuery.isLoading}
                        />
                      </Box>
                      <Button
                        onClick={addSelectedProduct}
                        disabled={!selectedProductValue}
                      >
                        {t("追加")}
                      </Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      {t(
                        "商品は今回の発注書にだけ一時追加されます。商品の仕入先設定や、次回以降の発注書には影響しません。",
                      )}
                    </Text>
                    {/* key＝仕入先・配送先が変わったら貼り付けの途中状態を破棄する
                        （addedLines と同じ理由＝旧拠点の在庫根拠を持ち越さない）。 */}
                    <PasteAddProducts
                      key={`paste-${selected.supplier_id}-${locationId ?? "primary"}`}
                      existingVariantIds={existingVariantIds}
                      locationId={locationId}
                      onAdd={addPastedProducts}
                    />
                  </BlockStack>
                </Card>
              )}

              {selected && effective && effective.lines.length > 0 && (
                <Card>
                  <LineSortSelect
                    value={sortKey}
                    onChange={setSortKey}
                    helpText={t(
                      "仕入先の請求書と同じ並びにすると、行を1つずつ突き合わせられます。発注書のPDF・CSV・バーコードラベルもこの並びで出ます。",
                    )}
                  />
                </Card>
              )}

              {selected && effective && effective.lines.length > 0 && (
                <InlineStack align="end">
                  <Button
                    onClick={handleBulkSaveCosts}
                    loading={bulkCostMutation.isPending}
                    disabled={Object.keys(costInputs).length === 0}
                  >
                    {t("仕入単価をまとめて保存")}
                  </Button>
                </InlineStack>
              )}

              {selected && effective && effective.lines.length > 0 && (
                <Card padding="0">
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "text",
                      "numeric",
                      "numeric",
                      "numeric",
                      "numeric",
                      ...(hasTaxRates ? (["text"] as const) : []),
                      "numeric",
                      "text",
                      "text",
                    ]}
                    headings={[
                      t("商品名"),
                      "SKU",
                      "ABC",
                      t("在庫"),
                      t("入荷予定"),
                      t("数量"),
                      t("単価"),
                      ...(hasTaxRates ? [t("税区分")] : []),
                      t("金額"),
                      t("サプライヤー変更"),
                      t("除外"),
                    ]}
                    rows={selectedWithAdded!.lines.map((l) => {
                      const edit = edits[l.variant_id];
                      const excluded = edit?.excluded ?? false;
                      // 表示値: 手入力バッファ → 自動注文書の推奨数量 → 空欄（手動注文書の未入力）。
                      const rawQty = qtyInputs[l.variant_id];
                      const qtyFieldValue =
                        rawQty !== undefined
                          ? rawQty
                          : l.recommended_qty !== null
                            ? String(l.recommended_qty)
                            : "";
                      // 金額計算用の数量（未入力は 0）。
                      const numericQty =
                        edit?.quantity ?? l.recommended_qty ?? 0;
                      const lineAmount = Number(l.unit_price) * numericQty;
                      return [
                        l.title ?? "—",
                        l.sku ?? "—",
                        l.abc_rank ? (
                          <Badge
                            key={`abc-${l.variant_id}`}
                            tone={
                              l.abc_rank === "A"
                                ? "success"
                                : l.abc_rank === "B"
                                  ? "info"
                                  : undefined
                            }
                          >
                            {l.abc_rank}
                          </Badge>
                        ) : (
                          "—"
                        ),
                        l.available.toString(),
                        l.incoming.toString(),
                        <BlockStack gap="050" key={`qty-${l.variant_id}`}>
                          <Box minWidth="90px">
                            <TextField
                              label={t("数量")}
                              labelHidden
                              type="number"
                              min={0}
                              value={qtyFieldValue}
                              placeholder={autoQuantity ? undefined : t("数量")}
                              disabled={excluded}
                              onChange={(v) => {
                                setQtyInputs((prev) => ({
                                  ...prev,
                                  [l.variant_id]: v,
                                }));
                                if (v === "") {
                                  updateEdit(l.variant_id, { quantity: 0 });
                                  return;
                                }
                                const n = Math.trunc(Number(v));
                                if (!Number.isNaN(n)) {
                                  updateEdit(l.variant_id, {
                                    quantity: Math.max(0, n),
                                  });
                                }
                              }}
                              autoComplete="off"
                            />
                          </Box>
                          {l.recommended_qty !== null &&
                            l.reorder_point !== null && (
                              <QuantityReason
                                reorderPoint={l.reorder_point}
                                replenishmentTarget={l.replenishment_target}
                                available={l.available}
                                incoming={l.incoming}
                                orderedOpen={l.ordered_open ?? 0}
                                recommendedQty={l.recommended_qty}
                                appliedMoq={l.applied_moq}
                                appliedPackSize={l.applied_pack_size}
                              />
                            )}
                        </BlockStack>,
                        <BlockStack gap="100" key={`cost-${l.variant_id}`}>
                          <Box minWidth="130px">
                            <TextField
                              label={t("単価")}
                              labelHidden
                              type="number"
                              min={0}
                              prefix={currencyPrefix}
                              // 未編集時は永続値を通貨の小数桁で表示（JPY は "720.00" → "720"）。
                              // 入力バッファ（costInputs）は打鍵どおり＝編集・保存の意味は不変。
                              value={
                                costInputs[l.variant_id] ??
                                moneyInputValue(
                                  l.unit_price,
                                  effective?.currency ?? DEFAULT_CURRENCY,
                                )
                              }
                              // 🔴 既定仕入先の単価が効いている行は編集させない。
                              // ここで入力しても cost_override は一度も読まれず、
                              // 「保存成功→表示が戻る」＝入力が無言で消えるだけになる。
                              disabled={
                                costMutation.isPending ||
                                l.is_supplier_cost === true
                              }
                              onChange={(v) =>
                                setCostInputs((prev) => ({
                                  ...prev,
                                  [l.variant_id]: v,
                                }))
                              }
                              onBlur={() => commitCost(l)}
                              autoComplete="off"
                            />
                          </Box>
                          <InlineStack gap="100" blockAlign="center">
                            {l.is_manual_cost && (
                              <Badge tone="info">{t("手動")}</Badge>
                            )}
                            {l.is_supplier_cost === true && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {t(
                                  "仕入先別の条件の単価を使用中（変更は「仕入先別の条件」から）",
                                )}
                              </Text>
                            )}
                            {l.is_estimated && (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {t("目安")}
                              </Text>
                            )}
                            {l.is_manual_cost && (
                              <Button
                                variant="plain"
                                onClick={() =>
                                  costMutation.mutate({
                                    variantId: l.variant_id,
                                    cost: null,
                                  })
                                }
                              >
                                {t("デフォルトに戻す")}
                              </Button>
                            )}
                          </InlineStack>
                        </BlockStack>,
                        ...(hasTaxRates
                          ? [
                              <Box minWidth="140px" key={`tax-${l.variant_id}`}>
                                {/* 🔴 選択肢は backend 配信（`tax_rates`）＝店ごとに違う。
                                    名前はマーチャントの設定文字列なので t() に通さない。 */}
                                <Select
                                  label={t("税区分")}
                                  labelHidden
                                  options={taxRateOptions}
                                  value={String(l.tax_rate_id ?? "")}
                                  disabled={taxMutation.isPending}
                                  onChange={(value) =>
                                    value === "" ||
                                    isStockyPublicBigIntId(value)
                                      ? taxMutation.mutate({
                                          productId: l.product_id,
                                          taxRateId:
                                            value === ""
                                              ? null
                                              : canonicalStockyPublicBigIntId(
                                                  value,
                                                ),
                                        })
                                      : undefined
                                  }
                                />
                              </Box>,
                            ]
                          : []),
                        excluded || qtyFieldValue === ""
                          ? "—"
                          : yen(lineAmount),
                        <Box minWidth="150px" key={`sup-${l.variant_id}`}>
                          <Select
                            label={t("サプライヤー変更")}
                            labelHidden
                            options={supplierChangeOptions}
                            value={String(selected.supplier_id)}
                            disabled={
                              supplierAssignMutation.isPending ||
                              suppliersQuery.isLoading
                            }
                            onChange={(value) =>
                              requestContextChange(() => {
                                resetTemporaryDraftChanges();
                                if (value === NEW_SUPPLIER_OPTION_VALUE) {
                                  navigate("/suppliers?add=1");
                                  return;
                                }
                                if (!isStockyPublicBigIntId(value)) return;
                                supplierAssignMutation.mutate({
                                  variantId: l.variant_id,
                                  supplierId:
                                    canonicalStockyPublicBigIntId(value),
                                });
                              })
                            }
                          />
                        </Box>,
                        <Box key={`ex-${l.variant_id}`}>
                          <Checkbox
                            label={t("除外")}
                            labelHidden
                            checked={excluded}
                            onChange={(checked) =>
                              updateEdit(l.variant_id, { excluded: checked })
                            }
                          />
                        </Box>,
                      ];
                    })}
                  />
                </Card>
              )}

              {(hasNextPage || hasPreviousPage) && (
                <InlineStack align="center">
                  <Pagination
                    label={`サプライヤー ${offset + 1}–${Math.min(
                      offset + drafts.length,
                      total,
                    )} / ${total}`}
                    hasPrevious={hasPreviousPage}
                    onPrevious={() =>
                      requestContextChange(() => {
                        resetTemporaryDraftChanges();
                        setOffset((o) =>
                          Math.max(0, o - PURCHASE_ORDER_PAGE_LIMIT),
                        );
                      })
                    }
                    hasNext={hasNextPage}
                    onNext={() =>
                      requestContextChange(() => {
                        resetTemporaryDraftChanges();
                        setOffset((o) => o + PURCHASE_ORDER_PAGE_LIMIT);
                      })
                    }
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Layout.Section>
        )}

        {selected && effective && effective.lines.length > 0 && (
          <Layout.Section variant="oneThird">
            <BlockStack gap="400">
              <CostSummaryCard
                taxGroups={effective.tax_groups}
                totalAmount={effective.total_amount}
                currency={effective.currency}
              />

              {stockyPoNoteTemplate !== null &&
                stockyPoNoteTemplate.text.length > 2000 && (
                  <Banner tone="warning">
                    {t(
                      "Stockyの発注書用メモは2,000文字を超えるため自動入力していません。「データ」で保存内容を確認し、必要な内容を短くして入力してください。",
                    )}
                  </Banner>
                )}

              <PoDetailsCard
                header={header}
                onChange={updateHeader}
                stockyTemplateApplied={
                  stockyPoNoteTemplate !== null &&
                  header.note === stockyPoNoteTemplate.text
                }
              />

              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    {t("サプライヤー情報")}
                  </Text>
                  {stockySupplierNotesQuery.isError && (
                    <Banner tone="warning">
                      {t(
                        "保存済みのStocky仕入先メモを確認できませんでした。「データ」で保存内容を確認してください。",
                      )}
                    </Banner>
                  )}
                  {stockyPrivateNote !== null && (
                    <Box
                      background="bg-surface-secondary"
                      borderRadius="200"
                      padding="300"
                    >
                      <BlockStack gap="150">
                        <Text as="h4" variant="headingSm">
                          {t("Stockyの仕入先メモ（社内用）")}
                        </Text>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          <Text as="p" variant="bodyMd">
                            {stockyPrivateNote.text}
                          </Text>
                        </div>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {t(
                            "UreyukiBox内だけに表示します。発注書・PDF・CSV・メールには含めません。",
                          )}
                        </Text>
                      </BlockStack>
                    </Box>
                  )}
                  {/* 登録番号は国の概念があるときだけ出す（Step 3）。JP＝適格請求書
                      （T+13 検証・未登録警告）、GB/DE/FR/AU/CA＝国の呼び名でラベルのみ
                      （書式は検証しない）、US・カタログ外＝出さない。 */}
                  {registration &&
                    (editingInvoice ? (
                      <BlockStack gap="200">
                        <TextField
                          label={
                            isJapanStore
                              ? t("サプライヤー 登録番号")
                              : registration.label
                          }
                          value={invoiceInput}
                          onChange={setInvoiceInput}
                          placeholder={
                            isJapanStore ? "T1234567890123" : undefined
                          }
                          autoComplete="off"
                          error={invoiceError}
                        />
                        <InlineStack gap="200">
                          <Button
                            variant="primary"
                            loading={invoiceMutation.isPending}
                            disabled={!!invoiceError}
                            onClick={() =>
                              invoiceMutation.mutate({
                                supplierId: selected.supplier_id,
                                value: invoiceInput.trim() || null,
                              })
                            }
                          >
                            {t("保存")}
                          </Button>
                          <Button
                            onClick={() => {
                              setEditingInvoice(false);
                              setInvoiceInput(
                                selected.invoice_registration_no ?? "",
                              );
                            }}
                          >
                            {t("取消")}
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    ) : (
                      <BlockStack gap="200">
                        {selected.invoice_registration_no ? (
                          <BlockStack gap="100">
                            {isJapanStore ? (
                              <Badge tone="success">
                                {t("適格請求書発行事業者")}
                              </Badge>
                            ) : (
                              <Text as="span" variant="bodySm" tone="subdued">
                                {registration.label}
                              </Text>
                            )}
                            <Text
                              as="span"
                              variant="bodyMd"
                              fontWeight="semibold"
                            >
                              {selected.invoice_registration_no}
                            </Text>
                          </BlockStack>
                        ) : isJapanStore ? (
                          // JP だけ警告調＝未登録は経過措置（控除減）の対象になるため。
                          <Badge tone="attention">
                            {t("サプライヤー 登録番号 未登録")}
                          </Badge>
                        ) : (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {registration.label}: —
                          </Text>
                        )}
                        <InlineStack>
                          <Button
                            variant="plain"
                            onClick={() => setEditingInvoice(true)}
                          >
                            {t("編集")}
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    ))}
                  <div ref={emailSectionRef}>
                    <Box
                      borderBlockStartWidth="025"
                      borderColor="border"
                      paddingBlockStart="300"
                    >
                      <BlockStack gap="200">
                        <Text as="h4" variant="headingSm">
                          {t("発注書メール")}
                        </Text>
                        {showSupplierEmailError && (
                          <Banner
                            tone="critical"
                            title={t(
                              "メール設定または送信を完了できませんでした",
                            )}
                            action={
                              supplierEmailNeedsReconciliation &&
                              selectedSupplierEmailAttempt?.disposition !==
                                "reconciliation_required"
                                ? {
                                    content: t("送信状況を確認"),
                                    onAction: () =>
                                      navigate(
                                        reconciliationDeliveryId === null
                                          ? "/purchase-order-email-reconciliation"
                                          : `/purchase-order-email-reconciliation/${reconciliationDeliveryId}`,
                                      ),
                                  }
                                : undefined
                            }
                          >
                            {supplierEmailError?.message ??
                              t("通信環境を確認して、もう一度お試しください。")}
                          </Banner>
                        )}
                        {recentPriorDeliveryId !== null &&
                          selectedSupplierEmailAttempt?.disposition !==
                            "reconciliation_required" &&
                          emailPreview === null && (
                            <Card>
                              <BlockStack gap="300">
                                <Banner
                                  tone="critical"
                                  title={
                                    duplicateSuppressed !== null
                                      ? t(
                                          "前回の送信済みメールを再表示しました",
                                        )
                                      : t("別注文メールの送信結果は未確定です")
                                  }
                                >
                                  {duplicateSuppressed !== null
                                    ? t(
                                        "新しいメールは送信していません。同じ注文なら操作は完了です。別の新規注文を送る場合だけ、前回と今回の全文を確認してください。",
                                      )
                                    : t(
                                        "新しい操作キーでは送信しないでください。保存済みの本文・操作キー・別注文確認情報を変えずに、同じ送信だけを再確認して再開できます。",
                                      )}
                                </Banner>
                                {priorDeliveryQuery.isLoading ? (
                                  <SkeletonBodyText lines={5} />
                                ) : !priorDeliveryIsDisplayable ? (
                                  <Banner
                                    tone="critical"
                                    title={t(
                                      "前回送信したメールの全文を確認できません",
                                    )}
                                    action={{
                                      content: t("再試行"),
                                      onAction: () =>
                                        void priorDeliveryQuery.refetch(),
                                    }}
                                  >
                                    {t(
                                      "前回の宛先・件名・本文・送信時刻を表示できるまで、別注文として送信できません。",
                                    )}
                                  </Banner>
                                ) : (
                                  <BlockStack gap="150">
                                    <Text as="p">
                                      {t("送信日時: {value}", {
                                        value: formatDateTime(
                                          priorDelivery.delivered_at!,
                                        ),
                                      })}
                                    </Text>
                                    <Text as="p">
                                      {t("宛先")}: {priorToEmail}
                                    </Text>
                                    <Text as="p">
                                      {t("件名")}: {priorSubject}
                                    </Text>
                                    <Box
                                      background="bg-surface-secondary"
                                      borderRadius="300"
                                      padding="300"
                                    >
                                      <pre
                                        style={{
                                          margin: 0,
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "anywhere",
                                          fontFamily: "inherit",
                                        }}
                                      >
                                        {priorText}
                                      </pre>
                                    </Box>
                                    <InlineStack align="end">
                                      <Button
                                        variant="primary"
                                        disabled={!priorDeliveryIsDisplayable}
                                        onClick={
                                          beginSeparatePurchaseOrderEmail
                                        }
                                      >
                                        {duplicateSuppressed !== null
                                          ? t("別の新規注文として送信する")
                                          : t("同じ別注文メールを再確認")}
                                      </Button>
                                    </InlineStack>
                                  </BlockStack>
                                )}
                              </BlockStack>
                            </Card>
                          )}
                        {selectedSupplierEmailAttempt !== null &&
                          (selectedSupplierEmailAttempt.disposition ===
                            "reconciliation_required" ||
                            selectedSupplierEmailAttempt.priorDeliveryId ===
                              null) &&
                          !emailDraftMutation.isSuccess && (
                            <Banner
                              tone={
                                selectedSupplierEmailAttempt.disposition ===
                                "reconciliation_required"
                                  ? "critical"
                                  : "warning"
                              }
                              title={
                                selectedSupplierEmailAttempt.disposition ===
                                "reconciliation_required"
                                  ? t(
                                      "この仕入先のメールは送信状況の確認が必要です",
                                    )
                                  : t(
                                      "この仕入先には結果未確定のメールがあります",
                                    )
                              }
                              action={
                                selectedSupplierEmailAttempt.disposition ===
                                "reconciliation_required"
                                  ? {
                                      content: t("送信状況を確認"),
                                      onAction: () =>
                                        navigate(
                                          selectedSupplierEmailAttempt.deliveryId ===
                                            null
                                            ? "/purchase-order-email-reconciliation"
                                            : `/purchase-order-email-reconciliation/${selectedSupplierEmailAttempt.deliveryId}`,
                                        ),
                                    }
                                  : {
                                      content: t("同じ内容を再確認"),
                                      onAction: openPurchaseOrderEmailPreview,
                                    }
                              }
                            >
                              {selectedSupplierEmailAttempt.disposition ===
                              "reconciliation_required"
                                ? t(
                                    "新しいメールとして送信したり、同じ操作を再試行したりせず、保存された送信記録を照合してください。",
                                  )
                                : t(
                                    "新しい本文や操作キーでは送信できません。保存済みの宛先・件名・本文・操作キーだけを再確認して再開できます。",
                                  )}
                            </Banner>
                          )}
                        {emailDraftMutation.isSuccess &&
                          duplicateSuppressed === null && (
                            <Banner tone="success">
                              {t("{email} に発注書を送信しました。", {
                                email: emailDraftMutation.data.to_email,
                              })}
                            </Banner>
                          )}
                        {editingContactEmail ? (
                          <BlockStack gap="200">
                            <TextField
                              label={t("仕入先の連絡先メール")}
                              type="email"
                              value={contactEmailInput}
                              onChange={setContactEmailInput}
                              autoComplete="email"
                            />
                            <InlineStack gap="200">
                              <Button
                                variant="primary"
                                loading={contactEmailMutation.isPending}
                                onClick={() =>
                                  contactEmailMutation.mutate({
                                    supplierId: selected.supplier_id,
                                    value: contactEmailInput.trim() || null,
                                  })
                                }
                              >
                                {t("保存")}
                              </Button>
                              <Button
                                onClick={() => setEditingContactEmail(false)}
                              >
                                {t("取消")}
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        ) : (
                          <BlockStack gap="200">
                            <Text as="p" variant="bodyMd">
                              {selectedSupplier?.contact_email ??
                                t("連絡先メール未登録")}
                            </Text>
                            <InlineStack gap="200">
                              <Button
                                variant="plain"
                                onClick={() => setEditingContactEmail(true)}
                              >
                                {t("メールアドレスを編集")}
                              </Button>
                              <Button
                                variant="primary"
                                loading={emailPreviewMutation.isPending}
                                disabled={
                                  !autoQuantity ||
                                  !selectedSupplier?.contact_email ||
                                  duplicateSuppressed !== null ||
                                  selectedSupplierEmailAttempt?.disposition ===
                                    "reconciliation_required"
                                }
                                onClick={openPurchaseOrderEmailPreview}
                              >
                                {selectedSupplierEmailAttempt === null
                                  ? t("発注メールを確認して送る")
                                  : selectedSupplierEmailAttempt.disposition ===
                                      "retryable"
                                    ? t("未確定の送信を再確認")
                                    : t("送信状況の確認が必要")}
                              </Button>
                            </InlineStack>
                            <Text as="p" variant="bodySm" tone="subdued">
                              {!autoQuantity
                                ? t(
                                    "発注書のメール送信は Starter プラン以上で利用できます。",
                                  )
                                : !selectedSupplier?.contact_email
                                  ? t(
                                      "仕入先のメールアドレスを登録すると、発注内容を確認して送信できます。",
                                    )
                                  : t(
                                      "サーバーで作成した宛先・件名・全文を確認したあとに送信します。この画面だけの数量変更・商品追加・除外は含まれません。",
                                    )}
                            </Text>
                          </BlockStack>
                        )}
                      </BlockStack>
                    </Box>
                  </div>
                </BlockStack>
              </Card>
            </BlockStack>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}

/**
 * Keep the existing calculated proposal workflow intact while giving native,
 * persisted drafts their own state boundary. A draft URL always wins over the
 * view toggle so a reload returns to the exact saved document.
 */
export function PurchaseOrders() {
  const [searchParams] = useSearchParams();
  return searchParams.get("view") === "saved" || searchParams.has("draft") ? (
    <SavedPurchaseOrderDrafts />
  ) : (
    <NewPurchaseOrderWorkspace />
  );
}
